import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { MapPin, Headphones, Globe, ArrowRight, Star, Volume2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const goToExplore = () => {
    window.location.assign("/explore");
  };

  const playWelcome = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }
    const welcome = new Audio("/welcome-culture-ken.m4a");
    audioRef.current = welcome;
    welcome.onended = () => {
      const bird = new Audio("/bird-sound.m4a");
      audioRef.current = bird;
      bird.onended = () => setIsPlaying(false);
      bird.onerror = () => setIsPlaying(false);
      bird.play().catch(() => setIsPlaying(false));
    };
    welcome.onerror = () => setIsPlaying(false);
    welcome.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
  };

  const features = [
    {
      icon: MapPin,
      title: "Location-Based Discovery",
      description: "Find fascinating places around you with our interactive map. Simply tap any location to learn its story."
    },
    {
      icon: Headphones,
      title: "Audio Summaries",
      description: "Listen to engaging, professionally narrated stories about each location. Perfect for walking tours."
    },
    {
      icon: Globe,
      title: "Rich Cultural Content",
      description: "AI-powered summaries combined with verified Wikipedia facts for accurate, comprehensive information."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Paper texture overlay */}
      <div className="fixed inset-0 paper-texture pointer-events-none" />
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src="/icon-192.png" 
                alt="Culture Ken"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-foreground">
                Culture Ken
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              {user ? (
                <Button 
                  onClick={goToExplore}
                  className="h-11 px-6 rounded-full font-medium"
                  data-testid="explore-btn"
                >
                  Explore <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : authLoading ? (
                // Don't flash "Sign In / Register" while we're still restoring
                // the session (Safari can take a moment on cold start).
                <div className="h-11 w-40 rounded-full bg-muted/50 animate-pulse" data-testid="landing-auth-loading" />
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate("/login")}
                    className="h-11 px-5 rounded-full font-medium"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => navigate("/signup")}
                    className="h-11 px-6 rounded-full font-medium"
                    data-testid="signup-btn"
                  >
                    Register
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-sm font-medium tracking-wide uppercase text-primary">
                  Your Personal Audio Guide
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
                  Discover the stories behind the culture
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                  Turn any location into an audio adventure. Rich, engaging summaries delivered straight to your ears.
                </p>
                <ul className="space-y-3 pt-1">
                  <li className="flex items-start gap-3">
                    <span className="mt-1 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-3 h-3 text-primary" />
                    </span>
                    <span className="text-base text-foreground/90">Bite-sized background info on any place of interest</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <Star className="w-3 h-3 text-primary" />
                    </span>
                    <span className="text-base text-foreground/90">Get the knowledge on vital facts, dates and people - for anywhere in the world!</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={goToExplore}
                  className="h-14 px-8 text-lg rounded-full font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  data-testid="hero-cta-btn"
                >
                  Start Exploring
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="h-14 px-8 text-lg rounded-full font-medium"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>
              
              {/* Social Proof */}
              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div 
                      key={i}
                      className="w-10 h-10 rounded-full bg-muted border-2 border-background"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Loved by thousands of curious travelers
                  </p>
                </div>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1678714873473-fb21921be332?w=800&q=80"
                  alt="Ancient cultural landmark"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card - plays welcome audio */}
              <button
                onClick={playWelcome}
                className="absolute -bottom-6 -left-6 bg-card rounded-xl p-4 shadow-lg border border-border/50 max-w-[200px] cursor-pointer hover:shadow-xl hover:scale-105 transition-all active:scale-95"
                data-testid="welcome-audio-btn"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isPlaying ? "bg-primary" : "bg-primary/10"}`}>
                    {isPlaying ? (
                      <Volume2 className="w-6 h-6 text-primary-foreground animate-pulse" />
                    ) : (
                      <Headphones className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-foreground">{isPlaying ? "Playing..." : "Audio Ready"}</p>
                    <p className="text-sm text-muted-foreground">Tap to listen</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              How Culture Ken Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Simple, intuitive, and designed for travelers of all ages
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card rounded-2xl p-8 shadow-sm border border-border/50 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-secondary rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-secondary-foreground mb-4">
              Ready to Explore?
            </h2>
            <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
              Sign up and start with a free audio summary.
            </p>
            <Button 
              size="lg"
              onClick={goToExplore}
              className="h-14 px-10 text-lg rounded-full font-medium bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              data-testid="cta-signup-btn"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/icon-192.png" 
                alt="Culture Ken"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold text-foreground">Culture Ken</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Culture Ken. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
